import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/api/photo.api');

/**
 * Photo API service for managing profile images and organization logos.
 * @module lib/api/photo.api
 */

import { apiClient } from './api-client';
import { fileApi } from './file.api';
import { createSingletonApi } from './singleton-factory';
import { clientPortalApi } from './client-portal.api';
import { getCurrentClientPortalContext } from './client-portal-context';
import { useAuth } from '@/store/auth.store';
import { ModuleTypeEnum } from '@scrambl/shared';
export interface PhotoUploadResponse {
  key: string;
url: string;
  fileName: string;
  contentType: string;
  size: number;
}

export interface PhotoUploadOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

interface BlobCacheEntry {
  blob: Blob | null;
  url: string | null;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class PhotoApiService {
  private blobCache = new Map<string, BlobCacheEntry>();

  /**
   * Gets the current user ID or throws
   */
  private getCurrentUserId(): string {
    const authStore = useAuth.getState();
    if (!authStore.user?.id) {
      throw new Error('You must be logged in');
    }
    return authStore.user.id;
  }

  /**
   * Uploads a profile photo to S3
   * @param file - File to upload
   * @returns Upload response
   */
  async uploadProfileImage(file: File): Promise<PhotoUploadResponse> {
    const userId = this.getCurrentUserId();

    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await apiClient<PhotoUploadResponse>(`/files/upload/profile/${userId}`, {
      method: 'POST',
      body: formData,
      headers: {},
    });

    this.clearUserImageCache(userId);
    return response;
  }

  /**
   * Uploads a profile photo with progress tracking
   * @param file - File to upload
   * @returns Upload response
   */
  async uploadProfileImageWithProgress(file: File): Promise<PhotoUploadResponse> {
    const userId = this.getCurrentUserId();

    const formData = new FormData();
    formData.append('file', file);

    return apiClient<PhotoUploadResponse>(`/files/upload/profile/${userId}`, {
      method: 'POST',
      body: formData,
      headers: {},
    });
  }

  /**
   * Updates a user's profile image with the S3 key
   * @param imageKey - S3 key of uploaded image
   */
  async updateProfileImage(imageKey: string): Promise<void> {
    const userId = this.getCurrentUserId();

    await apiClient(`/users/${userId}/profile-image`, {
      method: 'PUT',
      body: { imageKey },
      skipProfileImageCache: true,
    });

    this.clearUserImageCache(userId);
  }

  /**
   * Deletes a user's profile image
   * @param userId - User ID
   */
  async deleteProfileImage(userId: string): Promise<void> {
    await apiClient(`/users/${userId}/profile-image`, {
      method: 'DELETE',
      skipProfileImageCache: true,
    });
  }

  /**
   * Gets a user's profile image as a blob URL (cached)
   * @param userId - User ID
   * @returns Blob URL or null
   */
  async getProfileImageBlob(userId: string): Promise<string | null> {
    try {
      const cached = this.blobCache.get(userId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.url;
      }

      if (cached?.url) {
        URL.revokeObjectURL(cached.url);
      }
      this.blobCache.delete(userId);

      const { url: signedUrl } = await apiClient<{ url: string | null }>(
        `/users/${userId}/profile-image`,
        { skipProfileImageCache: true }
      );

      if (!signedUrl) {
        this.blobCache.set(userId, { blob: null, url: null, timestamp: Date.now() });
        return null;
      }

      let response: Response;
      try {
        response = await fetch(signedUrl);
      } catch {
        this.blobCache.set(userId, { blob: null, url: null, timestamp: Date.now() });
        return null;
      }

      if (!response.ok) {
        if (response.status === 403 || response.status === 404) {
          this.blobCache.set(userId, { blob: null, url: null, timestamp: Date.now() });
        }
        return null;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      this.blobCache.set(userId, { blob, url: blobUrl, timestamp: Date.now() });
      return blobUrl;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        this.blobCache.set(userId, { blob: null, url: null, timestamp: Date.now() });
        return null;
      }

      logger.error('[PhotoApi] Error getting profile image blob:', error);
      this.clearUserImageCache(userId);
      return null;
    }
  }

  /**
   * Clears cached image for a specific user
   * @param userId - User ID
   */
  clearUserImageCache(userId: string): void {
    const cached = this.blobCache.get(userId);
    if (cached?.url) {
      URL.revokeObjectURL(cached.url);
    }
    this.blobCache.delete(userId);
  }

  /**
   * Force refreshes a user's profile image
   * @param userId - User ID
   * @returns Fresh blob URL or null
   */
  async forceRefreshProfileImage(userId: string): Promise<string | null> {
    this.clearUserImageCache(userId);
    return this.getProfileImageBlob(userId);
  }

  /**
   * Clears all cached images
   */
  clearAllImageCache(): void {
    for (const [, cached] of this.blobCache.entries()) {
      if (cached.url) {
        URL.revokeObjectURL(cached.url);
      }
    }
    this.blobCache.clear();
  }

  /**
   * Handles CORS errors by clearing cache
   */
  handleCorsError(): void {
    logger.warn('[PhotoApi] CORS error detected, clearing all image cache');
    this.clearAllImageCache();
  }

  /**
   * Uploads and updates a user's profile image in one step
   * @param file - File to upload
   */
  async uploadAndUpdateProfileImage(file: File): Promise<void> {
    const uploadResult = await this.uploadProfileImage(file);
    await this.updateProfileImage(uploadResult.key);
  }

  /**
   * Gets multiple user profile images in one request
   * @param userIds - Array of user IDs
   * @returns Map of user IDs to image URLs
   */
  async getBulkProfileImages(userIds: string[]): Promise<Record<string, string | null>> {
    if (!userIds.length) return {};

    const uniqueUserIds = [...new Set(userIds)].filter((id) => id && typeof id === 'string');
    if (!uniqueUserIds.length) return {};

    try {
      const response = await apiClient<{ profileImages: Record<string, string | null> }>(
        '/users/profile-images/bulk',
        { method: 'POST', body: { userIds: uniqueUserIds } }
      );

      const now = Date.now();
      Object.entries(response.profileImages).forEach(([userId, url]) => {
        this.blobCache.set(userId, { blob: null, url, timestamp: now });
      });

      return response.profileImages;
    } catch (error) {
      logger.error('[PhotoApi] Error getting bulk profile images:', error);
      return {};
    }
  }

  /**
   * Uploads an organization logo to S3
   * @param organizationId - Organization ID
   * @param file - File to upload
   * @param options - Optional settings including explicit org headers for newly created orgs
   * @returns Upload response
   */
  async uploadOrganizationLogo(
    organizationId: string,
    file: File,
    options?: { useExplicitOrgHeaders?: boolean }
  ): Promise<PhotoUploadResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    // For newly created organizations, we need to explicitly pass org headers
    // since the organization isn't set in the store context yet
    const headers: Record<string, string> = {};
    if (options?.useExplicitOrgHeaders) {
      headers['x-organization-id'] = organizationId;
    }

    return apiClient<PhotoUploadResponse>(
      `/files/upload/organization/${organizationId}?asset=true`,
      { method: 'POST', body: formData, headers }
    );
  }

  /**
   * Updates an organization's logo with the S3 key
   * @param organizationId - Organization ID
   * @param imageKey - S3 key of uploaded image
   * @param options - Optional settings including explicit org headers for newly created orgs
   */
  async updateOrganizationLogo(
    organizationId: string,
    imageKey: string,
    options?: { useExplicitOrgHeaders?: boolean }
  ): Promise<void> {
    // For newly created organizations, we need to explicitly pass org headers
    const headers: Record<string, string> = {};
    if (options?.useExplicitOrgHeaders) {
      headers['x-organization-id'] = organizationId;
    }

    await apiClient(`/organizations/${organizationId}/logo`, {
      method: 'PUT',
      body: { imageKey },
      headers,
    });
  }

  /**
   * Deletes an organization logo
   * @param organizationId - Organization ID
   */
  async deleteOrganizationLogo(organizationId: string): Promise<void> {
    await apiClient(`/organizations/${organizationId}/logo`, { method: 'DELETE' });
  }

  /**
   * Uploads an image for inline use in rich text editors
   * @param file - File to upload
   * @param moduleType - Module type
   * @param moduleId - Module ID
   * @returns Upload response
   */
  async uploadInlineImage(
    file: File,
    moduleType: string = ModuleTypeEnum.TASK,
    moduleId?: string
  ): Promise<PhotoUploadResponse> {
    const userId = this.getCurrentUserId();
    const targetModuleId = moduleId || userId;
    const targetModuleType = moduleId ? moduleType : ModuleTypeEnum.MEMBER;

    const formData = new FormData();
    formData.append('file', file, file.name);

    const portalEntityId = getCurrentClientPortalContext()?.entityId;
    const portalSupportedModuleTypes = new Set(['note', 'thread', 'thread_comment']);
    if (portalEntityId && portalSupportedModuleTypes.has(targetModuleType.toLowerCase())) {
      return clientPortalApi.uploadFile(
        portalEntityId,
        targetModuleType.toLowerCase(),
        targetModuleId,
        file,
        true,
      ) as Promise<PhotoUploadResponse>;
    }

    return apiClient<PhotoUploadResponse>(
      `/files/upload/${targetModuleType.toLowerCase()}/${targetModuleId}?asset=true`,
      { method: 'POST', body: formData, headers: {} }
    );
  }

  /**
   * Gets multiple signed URLs for rich text images
   * @param keys - Array of S3 keys
   * @returns Map of keys to signed URLs
   */
  async getMultipleSignedUrls(keys: string[]): Promise<Record<string, string>> {
    if (!keys?.length) return {};

    const portalEntityId = getCurrentClientPortalContext()?.entityId;
    if (portalEntityId) {
      try {
        return await clientPortalApi.getSignedUrls(portalEntityId, keys);
      } catch (error) {
        logger.error('[PhotoApi] Error getting portal signed URLs:', error);
        return {};
      }
    }

    try {
      return await fileApi.getMultipleSignedUrls(keys);
    } catch (error) {
      logger.error('[PhotoApi] Error getting signed URLs:', error);
      try {
        const response = await apiClient<{ signedUrls: Record<string, string> }>(
          '/files/signed-urls',
          { method: 'POST', body: { keys } }
        );
        return response.signedUrls;
      } catch {
        return {};
      }
    }
  }

  /**
   * Force refreshes signed URLs for rich text images
   * @param keys - Array of S3 keys
   * @returns Map of keys to fresh signed URLs
   */
  async refreshSignedUrls(keys: string[]): Promise<Record<string, string>> {
    if (!keys?.length) return {};

    const portalEntityId = getCurrentClientPortalContext()?.entityId;
    if (portalEntityId) {
      try {
        return await clientPortalApi.getSignedUrls(portalEntityId, keys);
      } catch (error) {
        logger.error('[PhotoApi] Error refreshing portal signed URLs:', error);
        return {};
      }
    }

    try {
      return await fileApi.refreshSignedUrls(keys);
    } catch (error) {
      logger.error('[PhotoApi] Error refreshing signed URLs:', error);
      return {};
    }
  }
}

export const photoApi = createSingletonApi(PhotoApiService);
